import * as Types from 'types/types';

export type JobProcessingPhase = 'idle' | 'markdown' | 'requirements';

export const PHASE_LABEL: Record<JobProcessingPhase, string> = {
  idle: 'Preparing job analysis',
  markdown: 'Generating markdown',
  requirements: 'Extracting requirements',
};

const fieldValuePattern = (label: string): RegExp =>
  new RegExp(`^\\s*(?:[-*]\\s*)?(?:\\*\\*)?(?:${label})(?:\\*\\*)?\\s*:?\\s*(.+)$`, 'im');

const cleanMarkdownValue = (value: string): string =>
  value
    .replace(/^#+\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/[`*_]/g, '')
    .trim();

const getLabeledValue = (markdown: string, label: string): string => {
  const match = markdown.match(fieldValuePattern(label));
  return match ? cleanMarkdownValue(match[1]) : '';
};

const getFirstHeading = (markdown: string): string => {
  const match = markdown.match(/^#{1,2}\s+(.+)$/m);
  if (!match) return '';
  const heading = cleanMarkdownValue(match[1]);
  return /job description|job posting|position overview/i.test(heading) ? '' : heading;
};

const getFirstParagraph = (markdown: string): string => {
  const paragraph = markdown
    .split(/\n{2,}/)
    .find(block => cleanMarkdownValue(block).length > 40 && !/^\s*#/.test(block));
  return paragraph ? cleanMarkdownValue(paragraph.replace(/^[-*]\s*/gm, '')).slice(0, 500) : '';
};

export const previewDetailsFromMarkdown = (
  markdown: string
): Pick<Types.Job, 'title' | 'company' | 'summary'> => ({
  title: getLabeledValue(markdown, 'Job Title|Title|Position') || getFirstHeading(markdown),
  company: getLabeledValue(markdown, 'Company|Organization|Employer'),
  summary: getLabeledValue(markdown, 'Summary|Overview') || getFirstParagraph(markdown),
});

export function documentTypeFromFileName(fileName: string): Types.DocumentType | null {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'pdf';
    case 'docx':
      return 'docx';
    case 'md':
      return 'markdown';
    case 'txt':
      return 'txt';
    default:
      return null;
  }
}
